import { useState, useRef, useEffect, KeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/store/authStore";
import { useBrandingStore } from "@/store/brandingStore";
import { useSendOtp } from "@/api/otp";
import { brandingAssetUrl } from "@/api/branding";

export function MFAVerifyPage() {
  const brand = useBrandingStore((s) => s.branding);
  const brandLogo = brandingAssetUrl(brand.logo_url);
  const navigate = useNavigate();
  const verifyMfa = useAuthStore((s) => s.verifyMfa);
  const mfaSession = useAuthStore((s) => s.mfaSession);
  const sendOtp = useSendOtp();
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [sentInfo, setSentInfo] = useState<string | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (!mfaSession) {
      navigate("/login", { replace: true });
    }
  }, [mfaSession, navigate]);

  // App-OTP mode: trigger an email OTP as soon as the screen mounts. The user
  // landed here right after a successful password login, so it's still
  // "their" session.
  const isAppOtp = mfaSession === "APP_OTP";
  useEffect(() => {
    if (isAppOtp && !sendOtp.isPending && !sentInfo) {
      sendOtp.mutate(undefined, {
        onSuccess: (resp) => {
          setSentInfo(`Code sent to ${resp.masked_phone}`);
        },
        onError: (err) => {
          setError(err instanceof Error ? err.message : "Couldn't send code");
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAppOtp]);

  if (!mfaSession) {
    return null;
  }

  const handleSubmit = async (override?: string[]) => {
    const source = override ?? code;
    const fullCode = source.join("");
    if (fullCode.length !== 6 || !source.every((d) => d !== "")) return;

    setError(null);
    setLoading(true);
    try {
      await verifyMfa(fullCode);
      // Carry the deep link through the MFA step (MPPI-00828). Validated
      // the same way the sign-in page validates it: a same-site path only,
      // never "//host" which a browser reads as another origin.
      const raw = new URLSearchParams(window.location.search).get("returnUrl");
      let dest = "/";
      if (raw) {
        try {
          const path = decodeURIComponent(raw);
          if (path.startsWith("/") && !path.startsWith("//") &&
              !/^\/(login|mfa|set-new-password)\b/.test(path)) {
            dest = path;
          }
        } catch {
          dest = "/";
        }
      }
      navigate(dest);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Verification failed");
      setCode(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleInput = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);

    if (value && index < 5) {
      // Move focus to the next cell on each digit
      inputRefs.current[index + 1]?.focus();
    }

    // F-001: auto-submit when the 6th digit is entered. The operator
    // shouldn't need to grab the mouse for the Verify button. We pass
    // newCode through directly because React state updates are
    // asynchronous and `code` won't reflect this digit yet.
    if (index === 5 && value && newCode.every((d) => d !== "")) {
      void handleSubmit(newCode);
    }
  };

  const handleKeyDown = (
    index: number,
    e: KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
      return;
    }
    // F-001: pressing Enter at any cell submits if all digits are
    // present. Solves the "I've typed 6 digits, now where's the
    // submit button" friction.
    if (e.key === "Enter") {
      e.preventDefault();
      if (code.every((d) => d !== "")) {
        void handleSubmit();
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    e.preventDefault();
    const newCode = [...code];
    for (let i = 0; i < 6; i++) {
      newCode[i] = pasted[i] ?? "";
    }
    setCode(newCode);
    const focusIdx = Math.min(pasted.length, 5);
    inputRefs.current[focusIdx]?.focus();
    if (newCode.every((d) => d !== "")) {
      void handleSubmit(newCode);
    }
  };

  const isComplete = code.every((d) => d !== "");

  return (
    <div className="flex min-h-screen flex-col">
      {/* Top Header Bar — same as Login */}
      <header className="flex h-16 shrink-0 items-center border-b border-white/10 bg-primary px-4 lg:h-20 lg:px-8">
        <a href="/" className="flex items-center gap-3">
          {/* `/icon.png` is the WIDE wordmark, and a fixed h-10 w-10 box
              crushed it to an illegible sliver. Constrain the HEIGHT and let
              the width follow, capped so a wide mark cannot push the row.
              Same fix as Login.tsx, which had the identical bug.

              The company name is not repeated beside it: the wordmark already
              reads it. */}
          <img
            src={brandLogo ?? "/icon.png"}
            alt={brand.company_name}
            className="h-9 w-auto max-w-[200px] object-contain object-left"
          />
        </a>
      </header>

      <div className="flex flex-1">
        {/* Left Panel — Brand Showcase (hidden on mobile) */}
        <div className="hidden bg-navy-gradient lg:flex lg:w-[60%] lg:flex-col lg:items-center lg:justify-center lg:px-12">
          <img
            src={brandLogo ?? "/logo.png"}
            alt={brand.company_name}
            className="mb-8 h-44 w-auto object-contain"
          />
          <p className="mb-10 text-center font-display text-2xl text-accent">
            Administration Portal
          </p>
          <ul className="max-w-md space-y-4 text-base text-white">
            <li className="flex items-start gap-3">
              <span className="mt-1 block h-2 w-2 flex-shrink-0 rounded-full bg-accent" />
              Multi-factor authentication keeps the admin console secure
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-1 block h-2 w-2 flex-shrink-0 rounded-full bg-accent" />
              One-time codes are valid for 5 minutes
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-1 block h-2 w-2 flex-shrink-0 rounded-full bg-accent" />
              Pasting the 6-digit code is supported — no need to type each cell
            </li>
          </ul>
        </div>

        {/* Right Panel — OTP Form */}
        <div className="flex flex-1 flex-col items-center justify-center bg-[#FAFAFA] px-6 py-6 lg:w-[40%]">
          <div className="w-full max-w-sm">
            <h1 className="text-[30px] font-semibold leading-tight text-gray-900">
              Verify Your Identity
            </h1>
            <p className="mt-2 text-base text-gray-500">
              {isAppOtp
                ? "Enter the 6-digit code we just emailed you"
                : mfaSession === "SMS_MFA"
                  ? "Enter the 6-digit code we just sent to your phone"
                  : mfaSession === "SOFTWARE_TOKEN_MFA"
                    ? "Enter the 6-digit code from your authenticator app"
                    : "Enter your 6-digit verification code"}
            </p>

            {isAppOtp && (
              <p className="mt-2 text-xs text-gray-500">
                Email can take a minute to arrive. Check your spam folder
                if it hasn&rsquo;t shown up.
              </p>
            )}
            {mfaSession === "SMS_MFA" && (
              <p className="mt-2 text-xs text-gray-500">
                SMS can take up to a minute. Check your spam / blocked
                numbers if it hasn&rsquo;t arrived.
              </p>
            )}
            {sentInfo && (
              <p className="mt-2 text-xs text-emerald-700">{sentInfo}</p>
            )}

            {error && (
              <div
                className="mt-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
                role="alert"
              >
                {error}
              </div>
            )}

            <div className="mt-8">
              <label className="block text-sm font-medium text-gray-700">
                Verification Code
              </label>
              <div className="mt-2 flex justify-between gap-2">
                {code.map((digit, i) => (
                  <input
                    key={i}
                    ref={(el) => {
                      inputRefs.current[i] = el;
                    }}
                    type="text"
                    inputMode="numeric"
                    autoComplete={i === 0 ? "one-time-code" : "off"}
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleInput(i, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(i, e)}
                    onPaste={i === 0 ? handlePaste : undefined}
                    aria-label={`Digit ${i + 1} of 6`}
                    className="h-14 w-12 rounded-lg border border-gray-300 bg-white text-center text-xl font-semibold tracking-wide text-gray-900 transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50"
                    autoFocus={i === 0}
                  />
                ))}
              </div>
            </div>

            <button
              type="button"
              onClick={() => void handleSubmit()}
              disabled={!isComplete || loading}
              className="mt-6 flex h-12 w-full items-center justify-center rounded-lg bg-gold-gradient text-base font-semibold text-white shadow-sm transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:opacity-50"
            >
              {loading ? "Verifying…" : "Verify"}
            </button>

            {isAppOtp && (
              <button
                type="button"
                onClick={() => {
                  setError(null);
                  setSentInfo(null);
                  sendOtp.mutate(undefined, {
                    onSuccess: (resp) =>
                      setSentInfo(`New code sent to ${resp.masked_phone}`),
                    onError: (err) =>
                      setError(
                        err instanceof Error ? err.message : "Couldn't resend",
                      ),
                  });
                }}
                disabled={sendOtp.isPending}
                className="mt-4 w-full text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50"
              >
                {sendOtp.isPending ? "Sending…" : "Resend code"}
              </button>
            )}

            <p className="mt-8 text-center text-xs text-gray-400">
              &copy; {new Date().getFullYear()}{" "}
              {brand.copyright_text ?? brand.company_name}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
